import { get, has, mergeWith, isArray, cloneDeep, clone, filter } from "lodash";
import { createAnswerObject, currentAnswer } from "./questionStates";
import { getQuestionAnalysis } from "../helpers/analysis";

// when updating a question, need to deep clone it. This is a convenience method to get an
// answer object for updating by deep cloning it
function getAnswerForUpdating(state) {
    return cloneDeep(currentAnswer(state));
}

// updating an answer requires the questionHistory object to be updated with the newly
// updated answer object. Use this function and pass the state and answer object from getAnswerForUpdating()
function updateAnswer(state, answer, index = null) {
    let questionHistory = clone(state.questionHistory);
    if (index != null) {
        questionHistory[index] = answer;
    }
    else {
        questionHistory[state.currentAnswerIndex] = answer;
    }

    return {
        ...state,
        questionHistory: questionHistory
    };
}

const createRecord = (state, action) => {
    console.debug("Setting recordId to " + action.recordId);
    return { ...state, recordId: action.recordId };
};

const saveRecord = (state, action) => {
    console.debug("Saving record");
    return { ...state, record: action.record };
};

const loadQuestionSet = (state, action) => {
    console.debug("Loaded Question Set");
    return { ...state, questionSet: action.questionSet };
};

const initializeQuestionSet = state => {
    console.debug("Initializing Question Set");

    let firstQuestionID = state.questionSet.questionSet.defaultQuestionOrder[0];
    let questionMatches = filter(state.questionSet.questionSet.questions, (q) => q.questionID === firstQuestionID);
    let question = questionMatches[0];

    return {
        ...state,
        currentAnswerIndex: 0,
        questionHistory: [createAnswerObject(question, state)],
        workflowSession: {
            ...state.workflowSession,
            currentQuestionId: firstQuestionID
        }
    };
};

const initiateWorkflowSession = (state, action) => {
    console.debug("Initiated workflow session");

    let questionMatches = filter(state.questionSet.questionSet.questions, (q) => q.questionID === action.workflowSession.currentQuestionId);
    let question = questionMatches[0];

    return {
        ...state,
        currentAnswerIndex: 0,
        questionHistory: [createAnswerObject(question, state)],
        workflowSession: action.workflowSession,
        resumeMsgVisible: action.workflowSession.showResumeMessage,
        startTime: action.workflowSession.startTime
    };
};

const setCompanionWorkflowSessionId = (state, action) => {
    return {
        ...state,
        companionWorkflowSessionId: action.companionWorkflowSessionId
    };
};

const resumeWorkflowSession = (state, action) => {
    console.debug("Resume workflow session");

    return {
        ...state,
        resumeMsgVisible: action.showResumeMessage
    }
};

const hideResumeWorkflowMessage = (state) => {
    console.debug("Hide Resume workflow message");

    return {
        ...state,
        resumeMsgVisible: false
    }
};

const loadClientRequest = (state, action) => {
    console.debug("Loaded Client Request");
    return { ...state, clientRequest: action.clientRequest };
};

const loadWorkflowData = (state, action) => {
    console.debug("Loading workflowData");
    return {
        ...state,
        currentAnswerIndex: state.questionHistory ? action.workflowData.length : state.questionHistory.length - 1,
        questionHistory: state.questionHistory ? action.workflowData.concat(state.questionHistory) : action.workflowData,
        forwardAnswers: state.forwardAnswers ? action.forwardAnswers.concat(state.forwardAnswers) : action.forwardAnswers,
        startTime: (new Date()).getTime()
    };
};

const loadUIConfig = (state, action) => {
    console.debug("Loading UI Config");
    return {
        ...state,
        defaultFileStorageUrlPrefix: action.uiConfig.defaultFileStorageUrlPrefix,
        support: has(action, "uiConfig.support") ? action.uiConfig.support : {}
    };
};

const saveReferenceId = (state, action) => {
    console.debug("Saving referenceId");
    return { ...state, referenceId: action.referenceId };
};

const saveRequestId = (state, action) => {
    console.debug("Saving requestId");
    return { ...state, requestId: action.requestId };
};

const saveFilePath = (state, action) => {
    console.debug("Saving filepath: " + action.filepath);
    let questionHistory = clone(state.questionHistory);

    for (let i = 0 ; i < questionHistory.length; i++) {
        let newElement, found = false;

        if (has(questionHistory[i], "file") && questionHistory[i].file.uuid === action.uuid)
        {
            found = true;
            newElement = cloneDeep(questionHistory[i]);
            newElement.filepath = action.filepath;
        }

        if (found) {
            questionHistory[i] = newElement;
            break;
        }
    }

    return {
        ...state,
        questionHistory: questionHistory
    }
};

const saveAccessCode = (state, action) => {
    console.debug("Saving access code");
    return { ...state, accessCode: action.accessCode };
};

const saveOrganization = (state, action) => {
    console.debug("Saving organization");
    return { ...state, organization: action.organization };
};

const storeResultData = (state, action) => {
    console.debug("Adding external system result data to store");
    return { ...state, resultData: action.result };
}

const markMediaAsTampered = (state, action) => {
    console.debug("Mark media as tampered");

    let answer = state.questionHistory[action.answerIndex];
    if (!answer) {
        console.error("Unable to mark media as tampered. Unable to find corresponding answer at index: " + action.answerIndex);
        return;
    }

    if (!has(answer, "file")) {
        return {...state};
    }

    if (!has(answer, "tamperedMedia")) {
        answer.tamperedMedia = [];
    }

    console.debug("Marking file as tampered with uuid: " + answer.file.uuid);
    let tamperedFile = {
        file: answer.file,
        fileType: answer.fileType,
        tags: clone(answer.tags)
    };
    if (has(answer, "tamperedResult")) {
        tamperedFile.tamperedResult = answer.tamperedResult;
    }
    if (has(answer, "objectsDetectedResult")) {
        tamperedFile.objectsDetectedResult = answer.objectsDetectedResult;
    }
    if (has(answer, "damageDetectedResult")) {
        tamperedFile.damageDetectedResult = answer.damageDetectedResult;
    }
    answer.tamperedMedia.push(tamperedFile);

    delete answer.file;
    delete answer.fileType;
    delete answer.tamperedResult;
    delete answer.objectsDetectedResult;
    delete answer.damageDetectedResult;
    if (!tamperedFile.fileType.startsWith("image/")) {
        delete answer.tags;
    }

    return updateAnswer(state, answer, action.answerIndex);
};

const recordBillableOperation = (state, action) => {
    switch (action.operation) {
        case "barcodeScan":
            return {
                ...state,
                billableOperations: {
                    ...state.billableOperations,
                    barcodeScans: get(state, "billableOperations.barcodeScans", 0) + action.count
                }
            };
        case "textExtraction":
            return {
                ...state,
                billableOperations: {
                    ...state.billableOperations,
                    textExtractions: get(state, "billableOperations.textExtractions", 0) + action.count
                }
            };  
        case "facialDetection":
            return {
                ...state,
                billableOperations: {
                    ...state.billableOperations,
                    facialDetections: get(state, "billableOperations.facialDetections", 0) + action.count
                }
            };  
        default:
            return {
                ...state
            };
    }
};

const storeAnalysisResponse = (state, action) => {
    console.debug("Store analysis response");

    let answer = state.questionHistory[action.answerIndex];
    if (!answer) {
        console.error("Unable to store analysis result. Unable to find corresponding answer at index: " + action.answerIndex);
        return;
    }

    answer = cloneDeep(answer);

    // if the media has already been marked as tampered, then write the analysis response to the tamperedMedia
    // instead of writing it to the top level answer object
    if (has(answer, "file")) {
        answer[action.key] = action.response;
    }
    else if (has(answer, "tamperedMedia")) {
        let tamperedMedia = answer.tamperedMedia[answer.tamperedMedia.length - 1];
        tamperedMedia[action.key] = action.response;
    }
    else {
        console.warn("Was unable to store the analysis response because there was no file or tamperedMedia in the answer.");
    }

    return updateAnswer(state, answer, action.answerIndex);
};

const storeMediaFileData = (state, action) => {
    console.debug("Adding file data to store");

    let answer = getAnswerForUpdating(state);

    answer.fileType = action.file.type;
    if (action.isFullSizeImage || answer.fileType.startsWith("video/")
            || answer.fileType.startsWith("audio/" || answer.fileType === "application/pdf"))
    {
        answer.file = action.file;
    }
    answer.searchTerms = action.question.searchTerms ? action.question.searchTerms : [];
    answer.analysis = getQuestionAnalysis(action.question);

    return updateAnswer(state, answer);
};

// Find an existing file in the media array and add data to it
const appendMediaFileData = (state, uuid, data) => {
    let questionHistory = cloneDeep(state.questionHistory);

    for (let i = 0; i < questionHistory.length; i++) {
        if (has(questionHistory[i], "file") && uuid === questionHistory[i].file.uuid) {
            questionHistory[i] = mergeWith({}, questionHistory[i], data, (objValue, srcValue) => {
                if (isArray(objValue)) {
                    return objValue.concat(srcValue);
                }
                return undefined; // use default logic
            });
            return { ...state, questionHistory: questionHistory };
        }
    }

    return state;
};


export {
    createRecord,
    saveRecord,
    loadQuestionSet,
    initializeQuestionSet,
    initiateWorkflowSession,
    setCompanionWorkflowSessionId,
    resumeWorkflowSession,
    hideResumeWorkflowMessage,
    loadClientRequest,
    loadWorkflowData,
    loadUIConfig,
    recordBillableOperation,
    saveReferenceId,
    saveRequestId,
    saveFilePath,
    saveAccessCode,
    saveOrganization,
    storeAnalysisResponse,
    storeResultData,
    storeMediaFileData,
    markMediaAsTampered,
    appendMediaFileData
};
